/* tslint:disable:max-classes-per-file */
interface RetryableOptions {
  delay?: {
    initialTime?: number;
    maxTime?: number;
    exponentialBackoff?: boolean;
    skipFirstDelay?: boolean;
    jitter?: boolean;
  };
  retry?: {
    maxAttempts?: number;
    maxTime?: number;
    predicate?: (error: Error, attemptCount: number) => boolean | Promise<boolean>;
  };
}

class Retryable<T> {
  private attemptStartTime: number = new Date().getTime();
  private attemptCount: number = 0;

  constructor(private invocable: () => Promise<T>, private options: RetryableOptions = {}) {}

  public async execute(): Promise<T> {
    while (true) {
      try {
        await this.delayFor();
        return await this.invocable();
      } catch (error) {
        this.attemptCount++;

        if (!(await this.shouldRetry(error))) {
          throw error;
        }
      }
    }

    throw new Error('Something went wrong.');
  }

  private async shouldRetry(error: Error): Promise<boolean> {
    const { maxAttempts = Infinity, maxTime = Infinity, predicate } = this.options.retry || {};

    if (this.attemptCount >= maxAttempts || new Date().getTime() - this.attemptStartTime >= maxTime) {
      return false;
    }

    return predicate ? predicate(error, this.attemptCount) : !!error;
  }

  private delayFor(): Promise<void> {
    const { initialTime = 500, maxTime = Infinity, exponentialBackoff = true, skipFirstDelay = true, jitter = true } =
      this.options.delay || {};
    const baseDelayTime = jitter ? initialTime : initialTime / 2;
    const delayTime =
      this.attemptCount >= (skipFirstDelay ? 1 : 0)
        ? Math.min(maxTime, baseDelayTime * 2 ** (exponentialBackoff ? this.attemptCount : 1)) *
          (jitter ? Math.random() : 1)
        : 0;

    return new Promise((resolve) => window.setTimeout(resolve, delayTime));
  }
}

export class RetryableBreakError extends Error {}
export class RetryableAbortedError extends Error {}

export async function retryable<T>(invocable: () => Promise<T>, options: RetryableOptions = {}): Promise<T> {
  return new Retryable(invocable, options).execute();
}
